import { useCallback, useMemo } from "react";
import scrollIntoMessage from "../../../utils/scroll-to-message";
import { groupChannelTypeList } from "../constants";
import { useChatsUIContext } from "../context/ChatsContext";
import { getUserId } from "../functions";
import {
  getGroupChannelMessages,
  getPersonalMessages,
} from "../store/chatActions";
import { chatsSlice } from "../store/chatSlice";
import {
  ChatListType,
  ChatUserMetaData,
  ConversationsType,
  GetChannelChannelMessageType,
  SelectedTabType,
} from "../types";
import { ConversationTypeEnum } from "../types/enums";
import { useChatDispatch } from "./reduxHook";

const { actions } = chatsSlice;

const useSelectChat = () => {
  const dispatch = useChatDispatch();

  const chatsContext = useChatsUIContext();
  const chatsUIProps = useMemo(() => {
    return {
      navigatePage: chatsContext?.navigatePage,
      setNavigatePage: chatsContext?.setNavigatePage,
    };
  }, [chatsContext?.navigatePage, chatsContext?.setNavigatePage]);

  const getPersonalMessage = useCallback(
    ({
      userId,
      chatId,
      user,
      lastMessageId,
      page = 0,
    }: {
      userId: number;
      chatId?: string;
      user: ChatUserMetaData;
      lastMessageId?: string;
      page?: number;
    }) => {
      return new Promise((resolve) => {
        dispatch(getPersonalMessages(userId, chatId ?? "", page)).finally(
          () => {
            dispatch(actions.setMetaData(user));
            resolve(true);
            setTimeout(() => {
              scrollIntoMessage(lastMessageId!);
            }, 350);
          }
        );
      });
    },
    [dispatch]
  );

  const getGroupChannelMessage = useCallback(
    (props: GetChannelChannelMessageType) => {
      const { id, lastMessageId, page = 0 } = props;
      return new Promise((resolve) => {
        dispatch(getGroupChannelMessages(id, page)).finally(() => {
          resolve(true);
          setTimeout(() => {
            scrollIntoMessage(lastMessageId!);
          }, 350);
        });
      });
    },
    [dispatch]
  );

  const handleSelectChat = useCallback(
    ({
      chat,
      isResetNavigatePage = false,
      viewSelectedChat = false,
    }: {
      chat: ChatListType;
      isResetNavigatePage?: boolean;
      viewSelectedChat?: boolean;
    }) => {
      dispatch(actions.setEmptyAdjustedMessages());

      if (viewSelectedChat) dispatch(actions.viewSelectedChat(chat));
      else dispatch(actions.setSelectedChat(chat));

      if (chat?.type === ConversationTypeEnum.PERSONAL)
        getPersonalMessage({
          userId: getUserId(chat),
          chatId: chat?.chatId,
          user: chat?.user!,
        });
      else
        getGroupChannelMessage({
          id: chat?._id,
        });

      if (isResetNavigatePage) {
        chatsUIProps?.setNavigatePage?.({
          current: chat?.type as ConversationsType,
          key: [
            "home",
            groupChannelTypeList.includes(chat?.type as SelectedTabType)
              ? "group"
              : (chat?.type as SelectedTabType),
          ],
          history: [chat?.type as ConversationsType],
        });
      } else
        chatsUIProps?.setNavigatePage?.({
          current: chat?.type as ConversationsType,
          key: [
            ...(chatsUIProps?.navigatePage?.key || []),
            groupChannelTypeList.includes(chat?.type as SelectedTabType)
              ? "group"
              : (chat?.type as SelectedTabType),
          ],
          history: [
            ...(chatsUIProps?.navigatePage?.history || []),
            chat?.type as ConversationsType,
          ],
        });
    },
    [dispatch]
  );

  return { handleSelectChat, getPersonalMessage, getGroupChannelMessage };
};

export default useSelectChat;
